/**
 * The Legal holds tab's wire (owner decision 2026-10-16, migration 370).
 *
 * JSX-free on purpose, so root jest runs it (tests/unit/admin/legal-holds-api.test.ts).
 * The API answers with the result itself (no envelope) and refuses with RFC 9457
 * Problem Details; every code the routes send has its own sentence in the model.
 */
import { AdminApiError, adminFetch } from '@/providers/admin-api';
import { apiErrorMessage } from '@/lib/api-error';

import { LEGAL_HOLD_ERROR_TEXT, type LegalHoldList, type LegalHoldSubject } from './legal-holds-model';

export * from './legal-holds-model';

export const LEGAL_HOLD_PATHS = {
  list: '/v1/admin/moderation/legal-holds',
  action: (photoId: string, action: 'subject' | 'report') => `/v1/admin/moderation/legal-holds/${photoId}/${action}`,
} as const;

const FORBIDDEN_TEXT = 'You do not have permission for this action.';

export class LegalHoldActionError extends Error {
  constructor(
    public readonly status: number | null,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'LegalHoldActionError';
  }
}

function toLegalHoldError(e: unknown): LegalHoldActionError {
  if (e instanceof AdminApiError) {
    const body = e.body && typeof e.body === 'object' ? (e.body as { code?: unknown }) : undefined;
    const code = typeof body?.code === 'string' && body.code.length > 0 ? body.code : null;
    const known = code ? LEGAL_HOLD_ERROR_TEXT[code] : undefined;
    const message = known ?? (e.status === 403 ? FORBIDDEN_TEXT : apiErrorMessage(e.body, e.status));
    return new LegalHoldActionError(e.status, code, message);
  }
  return new LegalHoldActionError(null, null, e instanceof Error && e.message ? e.message : 'The request failed.');
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  try {
    const res = init ? await adminFetch(url, init) : await adminFetch(url);
    return (await res.json()) as T;
  } catch (e) {
    throw toLegalHoldError(e);
  }
}

export function fetchLegalHolds(apiUrl: string): Promise<LegalHoldList> {
  return call<LegalHoldList>(`${apiUrl}${LEGAL_HOLD_PATHS.list}`);
}

/** Audited server-side BEFORE the data is sent. */
export function viewLegalHoldSubject(apiUrl: string, photoId: string, reason: string): Promise<LegalHoldSubject> {
  return call(`${apiUrl}${LEGAL_HOLD_PATHS.action(photoId, 'subject')}`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason.trim() }),
  });
}

/** Set once; starts the 1-year clock of the member data. */
export function recordLegalHoldReport(
  apiUrl: string,
  photoId: string,
  reportId: string,
): Promise<{ report_id: string; reported_at: string; delete_after: string }> {
  return call(`${apiUrl}${LEGAL_HOLD_PATHS.action(photoId, 'report')}`, {
    method: 'POST',
    body: JSON.stringify({ report_id: reportId.trim() }),
  });
}
